import { ChevronDown, GripVertical } from 'lucide-react'
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { usePDFExport } from '../../hooks/use-pdf-export'
import { usePlatformConfig } from '../../providers/platform-provider'
import type { CV, UpdateCVRequest } from '../../types/cv'
import { PaginatedPreview } from '../preview/paginated-preview'
import {
  Button,
  type CVSection,
  Checkbox,
  ImageUpload,
  LayoutSelector,
  type LayoutType,
  PreviewControls,
  type PreviewMode,
  ResizablePanels,
  Select,
  TextArea,
  TextField,
} from '../ui'
import { cn } from '../ui/utils'

export interface PDFMargins {
  top: number
  bottom: number
  sides: number
}

export interface CVBuilderV2Props {
  cv: CV
  onChange: (data: UpdateCVRequest) => void
  className?: string
}

// Convert CV data to sections format
function cvToSections(_cvId?: string): CVSection[] {
  const sections: CVSection[] = []

  sections.push({
    id: 'personal',
    type: 'personal',
    title: 'Personal Information',
    visible: true,
  })

  sections.push({
    id: 'experience',
    type: 'experience',
    title: 'Work Experience',
    visible: true,
  })

  sections.push({
    id: 'education',
    type: 'education',
    title: 'Education',
    visible: true,
  })

  sections.push({
    id: 'skills',
    type: 'skills',
    title: 'Skills',
    visible: true,
  })

  sections.push({
    id: 'languages',
    type: 'languages',
    title: 'Languages',
    visible: true,
  })

  sections.push({
    id: 'certifications',
    type: 'certifications',
    title: 'Certifications',
    visible: true,
  })

  return sections
}

// Collapsible section card with drag handle
interface CollapsibleSectionCardProps {
  title: string
  children: ReactNode
  defaultExpanded?: boolean
  onDragStart: () => void
  onDragOver: () => void
  onDragEnd: () => void
  isDragging: boolean
  isDragOver: boolean
}

function CollapsibleSectionCard({
  title,
  children,
  defaultExpanded = false,
  onDragStart,
  onDragOver,
  onDragEnd,
  isDragging,
  isDragOver,
}: CollapsibleSectionCardProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragOver={(e) => {
        e.preventDefault()
        onDragOver()
      }}
      onDragEnd={onDragEnd}
      className={cn(
        'bg-white rounded-lg border transition-all duration-200',
        isDragging && 'opacity-50',
        isDragOver && 'border-primary-400 ring-2 ring-primary-100',
        !isDragging && !isDragOver && 'border-gray-200'
      )}
    >
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-100">
        <div className="cursor-grab text-gray-400 hover:text-gray-600">
          <GripVertical className="size-4" />
        </div>
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex-1 flex items-center gap-2 text-left"
        >
          <span className="font-medium text-gray-900 text-sm">{title}</span>
          <ChevronDown
            className={cn(
              'size-4 text-gray-400 ml-auto transition-transform duration-200',
              isExpanded && 'rotate-180'
            )}
          />
        </button>
      </div>
      {isExpanded && <div className="p-3">{children}</div>}
    </div>
  )
}

/**
 * CV Builder v2 - Modern UI with React Aria components
 */
export function CVBuilderV2({ cv, onChange, className = '' }: CVBuilderV2Props) {
  const { downloadPDF, isGenerating, error } = usePDFExport()
  const config = usePlatformConfig()
  const canExportPdf = config.features?.pdfExport ?? false

  // Preview state
  const [zoom, setZoom] = useState(50)
  const [previewMode, setPreviewMode] = useState<PreviewMode>('page')
  const [margins, setMargins] = useState<PDFMargins>({ top: 15, bottom: 15, sides: 20 })

  // Layout state
  const [layout, setLayout] = useState<LayoutType>('classic')

  // Section ordering - just track the order of section IDs
  const [sectionOrder, setSectionOrder] = useState<string[]>(() =>
    cvToSections(cv.id).map((s) => s.id)
  )
  const [draggedSection, setDraggedSection] = useState<number | null>(null)
  const [dragOverSection, setDragOverSection] = useState<number | null>(null)

  // Reset section order when CV changes (e.g., switching between sample/empty)
  useEffect(() => {
    setSectionOrder(cvToSections(cv.id).map((s) => s.id))
  }, [cv.id])

  // Derive sections from CV data, ordered by sectionOrder
  const sections = useMemo(() => {
    const allSections = cvToSections()
    const sectionMap = new Map(allSections.map((s) => [s.id, s]))
    return sectionOrder
      .map((id) => sectionMap.get(id))
      .filter((s): s is CVSection => s !== undefined)
  }, [sectionOrder])

  // Editor tab
  const [activeTab, setActiveTab] = useState<'content' | 'design' | 'settings'>('content')

  const handleExportPDF = async () => {
    const filename = cv.title ? `${cv.title.replace(/[^a-zA-Z0-9]/g, '_')}.pdf` : 'cv.pdf'
    await downloadPDF(cv.id, filename, { margins })
  }

  const handlePhotoChange = useCallback(
    (url: string | null) => {
      onChange({
        personalInfo: { ...cv.personalInfo, photoUrl: url || '', showPhoto: Boolean(url) },
      })
    },
    [cv.personalInfo, onChange]
  )

  const renderSectionEditor = (sectionId: string) => {
    switch (sectionId) {
      case 'personal':
        return (
          <div className="space-y-4">
            <div className="flex gap-4">
              <ImageUpload
                value={cv.personalInfo.photoUrl}
                onChange={handlePhotoChange}
                className="w-32 h-32"
              />
              <div className="flex-1 space-y-3">
                <TextField
                  label="Full Name"
                  value={cv.personalInfo.fullName}
                  onChange={(value) =>
                    onChange({ personalInfo: { ...cv.personalInfo, fullName: value } })
                  }
                  placeholder="John Doe"
                />
                <TextField
                  label="Job Title"
                  value={cv.personalInfo.jobTitle}
                  onChange={(value) =>
                    onChange({ personalInfo: { ...cv.personalInfo, jobTitle: value } })
                  }
                  placeholder="Software Engineer"
                />
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <TextField
                label="Email"
                value={cv.personalInfo.email}
                onChange={(value) =>
                  onChange({ personalInfo: { ...cv.personalInfo, email: value } })
                }
                placeholder="john@example.com"
              />
              <TextField
                label="Phone"
                value={cv.personalInfo.phone}
                onChange={(value) =>
                  onChange({ personalInfo: { ...cv.personalInfo, phone: value } })
                }
                placeholder="+1 234 567 890"
              />
              <TextField
                label="City"
                value={cv.personalInfo.city}
                onChange={(value) =>
                  onChange({ personalInfo: { ...cv.personalInfo, city: value } })
                }
                placeholder="New York"
              />
              <TextField
                label="Country"
                value={cv.personalInfo.country}
                onChange={(value) =>
                  onChange({ personalInfo: { ...cv.personalInfo, country: value } })
                }
                placeholder="USA"
              />
              <TextField
                label="Website"
                value={cv.personalInfo.website}
                onChange={(value) =>
                  onChange({ personalInfo: { ...cv.personalInfo, website: value } })
                }
                placeholder="https://johndoe.com"
              />
              <TextField
                label="LinkedIn"
                value={cv.personalInfo.linkedin}
                onChange={(value) =>
                  onChange({ personalInfo: { ...cv.personalInfo, linkedin: value } })
                }
                placeholder="linkedin.com/in/johndoe"
              />
            </div>

            <TextArea
              label="Professional Summary"
              value={cv.personalInfo.summary}
              onChange={(value) =>
                onChange({ personalInfo: { ...cv.personalInfo, summary: value } })
              }
              placeholder="A brief summary of your professional background..."
              rows={4}
            />
          </div>
        )

      case 'experience':
        return (
          <div className="space-y-4">
            {cv.experiences.map((exp, index) => (
              <div key={exp.id} className="p-4 bg-gray-50 rounded-lg space-y-3">
                <div className="flex justify-between items-start">
                  <span className="text-sm font-medium text-gray-500">Experience {index + 1}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onPress={() => {
                      onChange({
                        experiences: cv.experiences.filter((e) => e.id !== exp.id),
                      })
                    }}
                  >
                    Remove
                  </Button>
                </div>
                <div className="flex flex-col gap-3">
                  <TextField
                    label="Position"
                    value={exp.position}
                    onChange={(value) =>
                      onChange({
                        experiences: cv.experiences.map((e) =>
                          e.id === exp.id ? { ...e, position: value } : e
                        ),
                      })
                    }
                    placeholder="Software Engineer"
                  />
                  <TextField
                    label="Company"
                    value={exp.company}
                    onChange={(value) =>
                      onChange({
                        experiences: cv.experiences.map((e) =>
                          e.id === exp.id ? { ...e, company: value } : e
                        ),
                      })
                    }
                    placeholder="Acme Corp"
                  />
                  <TextField
                    label="Start Date"
                    value={exp.startDate}
                    onChange={(value) =>
                      onChange({
                        experiences: cv.experiences.map((e) =>
                          e.id === exp.id ? { ...e, startDate: value } : e
                        ),
                      })
                    }
                    placeholder="2020-01"
                  />
                  <TextField
                    label="End Date"
                    value={exp.endDate || ''}
                    onChange={(value) =>
                      onChange({
                        experiences: cv.experiences.map((e) =>
                          e.id === exp.id ? { ...e, endDate: value || null } : e
                        ),
                      })
                    }
                    placeholder="Present"
                  />
                </div>
                <Checkbox
                  isSelected={exp.isCurrent}
                  onChange={(isSelected) =>
                    onChange({
                      experiences: cv.experiences.map((e) =>
                        e.id === exp.id
                          ? { ...e, isCurrent: isSelected, endDate: isSelected ? null : e.endDate }
                          : e
                      ),
                    })
                  }
                >
                  I currently work here
                </Checkbox>
                <TextArea
                  label="Description"
                  value={exp.description}
                  onChange={(value) =>
                    onChange({
                      experiences: cv.experiences.map((e) =>
                        e.id === exp.id ? { ...e, description: value } : e
                      ),
                    })
                  }
                  placeholder="Describe your responsibilities..."
                  rows={3}
                />
              </div>
            ))}
            <Button
              variant="secondary"
              onPress={() => {
                onChange({
                  experiences: [
                    ...cv.experiences,
                    {
                      id: crypto.randomUUID(),
                      company: '',
                      position: '',
                      location: '',
                      startDate: '',
                      endDate: null,
                      isCurrent: false,
                      description: '',
                      sortOrder: cv.experiences.length,
                    },
                  ],
                })
              }}
              className="w-full"
            >
              + Add Experience
            </Button>
          </div>
        )

      case 'education':
        return (
          <div className="space-y-4">
            {cv.education.map((edu, index) => (
              <div key={edu.id} className="p-4 bg-gray-50 rounded-lg space-y-3">
                <div className="flex justify-between items-start">
                  <span className="text-sm font-medium text-gray-500">Education {index + 1}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onPress={() => {
                      onChange({
                        education: cv.education.filter((e) => e.id !== edu.id),
                      })
                    }}
                  >
                    Remove
                  </Button>
                </div>
                <div className="flex flex-col gap-3">
                  <TextField
                    label="Institution"
                    value={edu.institution}
                    onChange={(value) =>
                      onChange({
                        education: cv.education.map((e) =>
                          e.id === edu.id ? { ...e, institution: value } : e
                        ),
                      })
                    }
                    placeholder="University Name"
                  />
                  <TextField
                    label="Degree"
                    value={edu.degree}
                    onChange={(value) =>
                      onChange({
                        education: cv.education.map((e) =>
                          e.id === edu.id ? { ...e, degree: value } : e
                        ),
                      })
                    }
                    placeholder="Bachelor of Science"
                  />
                  <TextField
                    label="Field of Study"
                    value={edu.fieldOfStudy}
                    onChange={(value) =>
                      onChange({
                        education: cv.education.map((e) =>
                          e.id === edu.id ? { ...e, fieldOfStudy: value } : e
                        ),
                      })
                    }
                    placeholder="Computer Science"
                  />
                  <TextField
                    label="Grade/GPA"
                    value={edu.grade}
                    onChange={(value) =>
                      onChange({
                        education: cv.education.map((e) =>
                          e.id === edu.id ? { ...e, grade: value } : e
                        ),
                      })
                    }
                    placeholder="3.8/4.0"
                  />
                </div>
              </div>
            ))}
            <Button
              variant="secondary"
              onPress={() => {
                onChange({
                  education: [
                    ...cv.education,
                    {
                      id: crypto.randomUUID(),
                      institution: '',
                      degree: '',
                      fieldOfStudy: '',
                      location: '',
                      startDate: '',
                      endDate: null,
                      isCurrent: false,
                      description: '',
                      grade: '',
                      sortOrder: cv.education.length,
                    },
                  ],
                })
              }}
              className="w-full"
            >
              + Add Education
            </Button>
          </div>
        )

      case 'skills':
        return (
          <div className="space-y-3">
            {cv.skills.map((skill) => (
              <div key={skill.id} className="flex items-center gap-3">
                <TextField
                  value={skill.name}
                  onChange={(value) =>
                    onChange({
                      skills: cv.skills.map((s) => (s.id === skill.id ? { ...s, name: value } : s)),
                    })
                  }
                  placeholder="Skill name"
                  className="flex-1"
                />
                <Select
                  items={[
                    { id: 'beginner', name: 'Beginner' },
                    { id: 'intermediate', name: 'Intermediate' },
                    { id: 'advanced', name: 'Advanced' },
                    { id: 'expert', name: 'Expert' },
                  ]}
                  selectedKey={skill.level}
                  onSelectionChange={(key) =>
                    onChange({
                      skills: cv.skills.map((s) =>
                        s.id === skill.id ? { ...s, level: key as typeof skill.level } : s
                      ),
                    })
                  }
                  className="w-40"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onPress={() => {
                    onChange({
                      skills: cv.skills.filter((s) => s.id !== skill.id),
                    })
                  }}
                >
                  ×
                </Button>
              </div>
            ))}
            <Button
              variant="secondary"
              onPress={() => {
                onChange({
                  skills: [
                    ...cv.skills,
                    {
                      id: crypto.randomUUID(),
                      name: '',
                      level: 'intermediate',
                      category: '',
                      sortOrder: cv.skills.length,
                    },
                  ],
                })
              }}
              className="w-full"
            >
              + Add Skill
            </Button>
          </div>
        )

      case 'languages':
        return (
          <div className="space-y-3">
            {cv.languages.map((lang) => (
              <div key={lang.id} className="flex items-center gap-3">
                <TextField
                  value={lang.name}
                  onChange={(value) =>
                    onChange({
                      languages: cv.languages.map((l) =>
                        l.id === lang.id ? { ...l, name: value } : l
                      ),
                    })
                  }
                  placeholder="Language"
                  className="flex-1"
                />
                <Select
                  items={[
                    { id: 'native', name: 'Native' },
                    { id: 'fluent', name: 'Fluent' },
                    { id: 'advanced', name: 'Advanced' },
                    { id: 'intermediate', name: 'Intermediate' },
                    { id: 'basic', name: 'Basic' },
                  ]}
                  selectedKey={lang.proficiency}
                  onSelectionChange={(key) =>
                    onChange({
                      languages: cv.languages.map((l) =>
                        l.id === lang.id ? { ...l, proficiency: key as typeof lang.proficiency } : l
                      ),
                    })
                  }
                  className="w-40"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onPress={() => {
                    onChange({
                      languages: cv.languages.filter((l) => l.id !== lang.id),
                    })
                  }}
                >
                  ×
                </Button>
              </div>
            ))}
            <Button
              variant="secondary"
              onPress={() => {
                onChange({
                  languages: [
                    ...cv.languages,
                    {
                      id: crypto.randomUUID(),
                      name: '',
                      proficiency: 'intermediate',
                      sortOrder: cv.languages.length,
                    },
                  ],
                })
              }}
              className="w-full"
            >
              + Add Language
            </Button>
          </div>
        )

      case 'certifications':
        return (
          <div className="space-y-4">
            {cv.certifications.map((cert, index) => (
              <div key={cert.id} className="p-4 bg-gray-50 rounded-lg space-y-3">
                <div className="flex justify-between items-start">
                  <span className="text-sm font-medium text-gray-500">
                    Certification {index + 1}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onPress={() => {
                      onChange({
                        certifications: cv.certifications.filter((c) => c.id !== cert.id),
                      })
                    }}
                  >
                    Remove
                  </Button>
                </div>
                <div className="flex flex-col gap-3">
                  <TextField
                    label="Name"
                    value={cert.name}
                    onChange={(value) =>
                      onChange({
                        certifications: cv.certifications.map((c) =>
                          c.id === cert.id ? { ...c, name: value } : c
                        ),
                      })
                    }
                    placeholder="AWS Solutions Architect"
                  />
                  <TextField
                    label="Issuer"
                    value={cert.issuer}
                    onChange={(value) =>
                      onChange({
                        certifications: cv.certifications.map((c) =>
                          c.id === cert.id ? { ...c, issuer: value } : c
                        ),
                      })
                    }
                    placeholder="Amazon Web Services"
                  />
                </div>
              </div>
            ))}
            <Button
              variant="secondary"
              onPress={() => {
                onChange({
                  certifications: [
                    ...cv.certifications,
                    {
                      id: crypto.randomUUID(),
                      name: '',
                      issuer: '',
                      issueDate: '',
                      expiryDate: null,
                      credentialId: '',
                      credentialUrl: '',
                      sortOrder: cv.certifications.length,
                    },
                  ],
                })
              }}
              className="w-full"
            >
              + Add Certification
            </Button>
          </div>
        )

      default:
        return <div className="text-gray-500">Select a section to edit</div>
    }
  }

  // Render the editor panel
  const editorPanel = (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200">
        <TextField
          value={cv.title}
          onChange={(value) => onChange({ title: value })}
          placeholder="CV Title"
          inputClassName="text-lg font-semibold border-none bg-transparent px-0"
        />
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        {(['content', 'design', 'settings'] as const).map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            className={cn(
              'flex-1 px-4 py-3 text-sm font-medium capitalize',
              'border-b-2 transition-colors',
              activeTab === tab
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            )}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'content' && (
          <div className="p-4 space-y-3">
            {/* All sections as collapsible cards */}
            {sections
              .filter((s) => s.visible)
              .map((section, index) => (
                <CollapsibleSectionCard
                  key={section.id}
                  title={section.title}
                  defaultExpanded={index === 0}
                  onDragStart={() => setDraggedSection(index)}
                  onDragOver={() => setDragOverSection(index)}
                  onDragEnd={() => {
                    if (
                      draggedSection !== null &&
                      dragOverSection !== null &&
                      draggedSection !== dragOverSection
                    ) {
                      const newOrder = [...sectionOrder]
                      const [removed] = newOrder.splice(draggedSection, 1)
                      if (!removed) return
                      newOrder.splice(dragOverSection, 0, removed)
                      setSectionOrder(newOrder)
                    }
                    setDraggedSection(null)
                    setDragOverSection(null)
                  }}
                  isDragging={draggedSection === index}
                  isDragOver={
                    dragOverSection === index && draggedSection !== null && draggedSection !== index
                  }
                >
                  {renderSectionEditor(section.id)}
                </CollapsibleSectionCard>
              ))}
          </div>
        )}

        {activeTab === 'design' && (
          <div className="p-4 space-y-6">
            <LayoutSelector value={layout} onChange={setLayout} />

            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-700">Margins (mm)</h4>
              <div className="grid grid-cols-3 gap-3">
                <TextField
                  label="Top"
                  value={String(margins.top)}
                  onChange={(value) => setMargins({ ...margins, top: Number(value) })}
                />
                <TextField
                  label="Bottom"
                  value={String(margins.bottom)}
                  onChange={(value) => setMargins({ ...margins, bottom: Number(value) })}
                />
                <TextField
                  label="Sides"
                  value={String(margins.sides)}
                  onChange={(value) => setMargins({ ...margins, sides: Number(value) })}
                />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="p-4 space-y-4">
            <TextField
              label="Template ID"
              value={cv.templateId}
              onChange={(value) => onChange({ templateId: value })}
            />
            <TextField
              label="Locale"
              value={cv.locale}
              onChange={(value) => onChange({ locale: value })}
            />
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200">
        {error && <p className="text-sm text-red-600 mb-3">{error.message}</p>}
        {canExportPdf && (
          <Button onPress={handleExportPDF} isDisabled={isGenerating} className="w-full">
            {isGenerating ? 'Generating PDF...' : 'Export PDF'}
          </Button>
        )}
      </div>
    </div>
  )

  // Render the preview panel
  const previewPanel = (
    <div className="flex flex-col h-full bg-gray-200">
      {/* Preview Controls */}
      <PreviewControls
        zoom={zoom}
        onZoomChange={setZoom}
        mode={previewMode}
        onModeChange={setPreviewMode}
        pageCount={2}
        currentPage={1}
      />

      {/* Preview Area */}
      <div
        className={cn(
          'flex-1 overflow-auto p-4',
          previewMode === 'overview' && 'flex flex-wrap gap-4 justify-center content-start',
          previewMode === 'focus' && 'flex items-center justify-center'
        )}
      >
        <PaginatedPreview
          cv={cv}
          scale={previewMode === 'overview' ? 0.3 : zoom / 100}
          margins={margins}
        />
      </div>
    </div>
  )

  // Focus mode renders only the preview
  if (previewMode === 'focus') {
    return (
      <div className={cn('h-screen bg-gray-900', className)}>
        <div className="flex flex-col h-full">
          {/* Minimal controls bar */}
          <div className="flex items-center justify-between px-4 py-2 bg-gray-800">
            <PreviewControls
              zoom={zoom}
              onZoomChange={setZoom}
              mode={previewMode}
              onModeChange={setPreviewMode}
              pageCount={2}
              currentPage={1}
              className="!bg-transparent !border-0"
            />
          </div>

          {/* Full preview */}
          <div className="flex-1 overflow-auto p-8 flex items-center justify-center">
            <PaginatedPreview cv={cv} scale={zoom / 100} margins={margins} />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={cn('h-screen bg-gray-100', className)}>
      <ResizablePanels
        left={editorPanel}
        right={previewPanel}
        defaultLeftWidth={420}
        minLeftWidth={320}
        maxLeftWidth={600}
        keyboardStep={40}
      />
    </div>
  )
}
